import { useEffect, useMemo, useRef } from "react";
import {
  View,
  Text,
  Pressable,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  ActivityIndicator,
  Linking,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import Privacy from "../../components/Privacy";
import { useNavigation, useRoute } from "@react-navigation/native";
import Constants from "expo-constants";
import * as TermsClients from "../../config/terms/clients";
import * as TermsBlix from "../../config/terms/blix";
import * as TermsGeneralApp from "../../config/terms/generalApp";
import { ADMIN_COLORS, ADMIN_FONT } from "../../config/theme/adminColors";
import { useCustomTheme } from "../../contexts/useCustomTheme";
import { resolveTermsOfUseUrl } from "../../utils/termsOfUse";

/**
 * Resolve export de dados de termos/política com fallback:
 * - preferimos termsOfUseData / privacyPolicyData
 * - se ainda não existir, cai no privacyData (seu padrão atual)
 */
function resolveTermsAndPrivacy(moduleObj) {
  const terms = moduleObj?.termsOfUseData || moduleObj?.privacyData || [];
  const privacy = moduleObj?.privacyPolicyData || moduleObj?.privacyData || [];
  return { terms, privacy };
}

export function TermsofUse({ isAdmin }) {
  const navigation = useNavigation();
  const route = useRoute();
  const { customColor, customInfo, isLoadingCustomInfo } = useCustomTheme();

  const { width } = useWindowDimensions();
  const isNarrow = width < 480;

  // ✅ doc padrão = terms (não quebra navegação atual)
  const doc = route?.params?.doc === "privacy" ? "privacy" : "terms";

  const extra = Constants?.manifest?.extra ?? Constants?.expoConfig?.extra ?? {};

  const guruUserToken = extra?.guruUserToken;
  const pagarmeApiKey = extra?.pagarmeApiKey;

  const isClientGeneral = !!(guruUserToken || pagarmeApiKey);

  // Chrome neutro (cinzas) para todos. O destaque da aba ativa usa a MARCA DO CLIENTE
  // na visão do aluno e um tom neutro escuro na visão do administrador.
  const accent = isAdmin ? ADMIN_COLORS.ink : customColor?.primary || ADMIN_COLORS.ink;

  // Termos PRÓPRIOS do cliente (painel: Minha conta » "URL dos seus termos de
  // uso"). Quando cadastrados, substituem o texto padrão desta tela na visão do
  // aluno — logado ou não. O administrador continua vendo o contrato da Blix,
  // que não é substituível.
  const clientTermsUrl = isAdmin ? "" : resolveTermsOfUseUrl(customInfo);

  // A leitura de `customization/infos` é assíncrona: sem esperar, a tela
  // montaria o texto padrão e só depois pularia para o link do cliente — que é
  // exatamente o "aparece o termo antigo" relatado por quem abre deslogado,
  // sem nada em cache.
  const isResolvingClientTerms = !isAdmin && isLoadingCustomInfo;

  const hasRedirected = useRef(false);

  useEffect(() => {
    if (!clientTermsUrl || hasRedirected.current) return;
    hasRedirected.current = true;

    // Troca a aba atual em vez de `window.open`: esta tela É o documento (o
    // aluno chegou aqui pelo menu ou por link direto), e abrir em nova aba
    // dependeria de um clique — num efeito o navegador bloquearia o popup.
    // `replace` e não `assign`: com `assign`, o botão voltar do navegador
    // devolveria a esta rota, que redirecionaria de novo — o aluno ficaria preso.
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.location.replace(clientTermsUrl);
      return;
    }
    Linking.openURL(clientTermsUrl).catch(() => {});
  }, [clientTermsUrl]);

  const { terms: termsClient, privacy: privacyClient } = resolveTermsAndPrivacy(TermsClients);
  const { terms: termsBlix, privacy: privacyBlix } = resolveTermsAndPrivacy(TermsBlix);
  const { terms: termsGeneral, privacy: privacyGeneral } = resolveTermsAndPrivacy(TermsGeneralApp);

  const contentData = useMemo(() => {
    // Admin → documentos Blix
    if (isAdmin) {
      return doc === "privacy" ? privacyBlix : termsBlix;
    }
    // Cliente "generalApp" → documentos generalApp
    if (isClientGeneral) {
      return doc === "privacy" ? privacyGeneral : termsGeneral;
    }
    // Cliente padrão → documentos clients
    return doc === "privacy" ? privacyClient : termsClient;
  }, [
    doc,
    isAdmin,
    isClientGeneral,
    privacyBlix,
    termsBlix,
    privacyGeneral,
    termsGeneral,
    privacyClient,
    termsClient,
  ]);

  const Tab = ({ value, label }) => {
    const active = doc === value;
    return (
      <Pressable
        onPress={() => navigation.setParams?.({ doc: value })}
        accessibilityRole="button"
        accessibilityLabel={label}
        hitSlop={8}
        style={({ hovered }) => [
          styles.tab,
          isNarrow && styles.tabCompact,
          active && { backgroundColor: accent },
          !active && hovered && { backgroundColor: ADMIN_COLORS.secondaryBg },
        ]}
      >
        <Text
          numberOfLines={1}
          style={[
            styles.tabText,
            isNarrow && { fontSize: 12 },
            { color: active ? "#FFFFFF" : ADMIN_COLORS.ink },
          ]}
        >
          {label}
        </Text>
      </Pressable>
    );
  };

  return (
    <SafeAreaView style={styles.safe}>
      {/* Barra superior */}
      <View style={styles.topBar}>
        <View style={[styles.segmentGroup, isNarrow && { flex: 1 }]}>
          <Tab value="terms" label="Termos de uso" />
          <Tab value="privacy" label="Política de privacidade" />
        </View>

        <Pressable
          onPress={() => navigation.goBack()}
          hitSlop={10}
          accessibilityRole="button"
          accessibilityLabel="Fechar"
          style={({ hovered }) => [
            styles.closeBtn,
            hovered && { backgroundColor: ADMIN_COLORS.secondaryBg },
          ]}
        >
          <Ionicons name="close" size={22} color={ADMIN_COLORS.ink} />
        </Pressable>
      </View>

      <ScrollView contentContainerStyle={styles.scrollContent}>
        {isResolvingClientTerms || clientTermsUrl ? (
          <View style={[styles.docCard, styles.redirectCard]}>
            <ActivityIndicator color={accent} />
            <Text style={styles.redirectText}>
              {clientTermsUrl
                ? "Abrindo os termos de uso..."
                : "Carregando os termos de uso..."}
            </Text>
            {/* Rede lenta ou redirecionamento bloqueado: o link fica à mão. */}
            {!!clientTermsUrl && (
              <Pressable onPress={() => Linking.openURL(clientTermsUrl)} hitSlop={8}>
                <Text style={[styles.redirectLink, { color: accent }]}>
                  Clique aqui se a página não abrir
                </Text>
              </Pressable>
            )}
          </View>
        ) : (
          <View style={styles.docCard}>
            <Privacy privacyData={contentData} />
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
  },
  topBar: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: ADMIN_COLORS.surface,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.border,
  },
  segmentGroup: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  tab: {
    minHeight: 40,
    paddingVertical: 9,
    paddingHorizontal: 16,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    backgroundColor: ADMIN_COLORS.surface,
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
  },
  tabCompact: {
    flexGrow: 1,
  },
  tabText: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: "700",
    letterSpacing: 0.3,
  },
  closeBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  scrollContent: {
    flexGrow: 1,
    padding: 24,
    alignItems: "center",
  },
  docCard: {
    width: "100%",
    maxWidth: 820,
    backgroundColor: ADMIN_COLORS.surface,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: ADMIN_COLORS.radius,
    paddingVertical: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
  },
  redirectCard: {
    alignItems: "center",
    gap: 12,
    paddingVertical: 48,
    paddingHorizontal: 24,
  },
  redirectText: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.ink,
    textAlign: "center",
  },
  redirectLink: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: "700",
    textDecorationLine: "underline",
    textAlign: "center",
    cursor: "pointer",
  },
});
